import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-explicit-any */
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { CatButton } from "blocks-nextjs/blocks-react/components/Button/Button";
import { CatContextualMenu } from "blocks-nextjs/blocks-react/components/ContextualMenu/ContextualMenu";
import { CatDropdown } from "blocks-nextjs/blocks-react/components/Dropdown/Dropdown";
import { CatFieldset } from "blocks-nextjs/blocks-react/components/Fieldset/Fieldset";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatIconClock } from "blocks-nextjs/blocks-react/components/Icons/Clock/Clock";
import { CatIconClose } from "blocks-nextjs/blocks-react/components/Icons/Close/Close";
import { CatInputField } from "blocks-nextjs/blocks-react/components/InputField/InputField";
import { CatList } from "blocks-nextjs/blocks-react/components/List/List";
import { CatListItem } from "blocks-nextjs/blocks-react/components/ListItem/ListItem";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import { CatRadioField } from "blocks-nextjs/blocks-react/components/RadioField/RadioField";
import { CatSegmentedControl } from "blocks-nextjs/blocks-react/components/SegmentedControl/SegmentedControl";
import { CatSegmentedControlItem } from "blocks-nextjs/blocks-react/components/SegmentedControlItem/SegmentedControlItem";
import React, { useEffect, useRef, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { prepareFutureChargeOptionsRequestBody } from "../../components/OpsPLanner/ChargingOptions.helper";
import { useAssetsChargingOptions, useFutureChargingOptions } from "../../services/query/eflow-charge-operation-planner-api.query";
import { safelyStopImmediatePropagation } from "../../shared/eventUtils";
import { getDefaultTimeUnits, getHourMinuteForTimezone, getLocalTimeInCESTFormat, getPlaceholderTime, getTimezoneAbbr, sortByChargingOptions } from "../../shared/helper";
import useAppConfigStore from "../../store/useAppConfigStore";
import { useChargingPersistenceStore } from "../../store/useChargingPersistenceStore";
import APIError from "../APIError/APIError";
import NoDataMessage from "../DataError/NoDataMessage";
import ChargingOptions from "./ChargingOptions";
import FutureChargingOptions from "./FutureChargingOptions";
const LiveLocalCESTTime = ({ timezone = "Europe/Amsterdam" }) => {
    const [time, setTime] = useState(getLocalTimeInCESTFormat(timezone));
    useEffect(() => {
        const interval = setInterval(() => {
            setTime(getLocalTimeInCESTFormat(timezone));
        }, 1000);
        return () => clearInterval(interval);
    }, [timezone]);
    return `${time} ${getTimezoneAbbr(timezone)}`;
};
const ChargingOptionsWrapper = (props) => {
    const { assetId, offset, projectId, showTitle = true, sizeSmall = false, timezone } = props;
    const STALE_FUTURE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minute
    const [chargeFilter, setChargeFilter] = useState("80");
    const [sortByFilter, setSortByFilter] = useState("fastest");
    const [fetchFutureChargeOptions, setFetchFutureChargeOptions] = useState(false);
    const [timeUnits, setTimeUnits] = useState(getDefaultTimeUnits(timezone));
    const [showFutureOptions, setShowFutureOptions] = useState(false);
    const [isLoadingFutureOptions, setIsLoadingFutureOptions] = useState(false);
    const [selectedTime, setSelectedTime] = useState("Select time");
    const [searchParams] = useSearchParams();
    const [openSelectTime] = useState(false);
    const selectTimeRef = useRef(null);
    const queryClient = useQueryClient();
    const assetKey = assetId || "__default__";
    // Will be decided in effect per-asset
    const [fetchChargingData, setFetchChargingData] = useState(false);
    const [isReloadingChargingOptions, setIsReloadingChargingOptions] = useState(false); // loader for base charging options
    const suppressNextOpenRef = useRef(false);
    // Ensure we only auto-trigger one fresh base fetch on mount for 'no time selected' case
    const initialBaseFetchRef = useRef(false);
    // Prevent repeated stale-trigger refetch loops
    const staleRefetchTriggeredRef = useRef(false);
    // Track if user manually changed time panel, so hydration won't overwrite
    const userChangedTimePanelRef = useRef(false);
    const lastAssetKeyRef = useRef(assetKey);
    const initialTimePanel = "Departure";
    const panelIndexMap = {
        Arrival: 1,
        Completion: 2,
        Departure: 0
    };
    const initialTimeIndex = panelIndexMap[initialTimePanel];
    const [timePanel, setTimePanel] = useState(initialTimePanel);
    const [timePanelActiveIndex, setTimePanelActiveIndex] = useState(initialTimeIndex);
    const [timeError, setTimeError] = useState("");
    const TimeSchema = z.object({
        time: z.string().trim().min(1, "Time is required")
    });
    const ValidationSchema = TimeSchema;
    const defaultValuesInfo = {
        time: ""
    };
    const { control, getValues, setValue } = useForm({
        defaultValues: defaultValuesInfo,
        mode: "onBlur",
        resolver: zodResolver(ValidationSchema)
    });
    const { appConfig } = useAppConfigStore((state) => ({
        appConfig: state.appConfig
    }));
    const { data: assetChargingOptionsData = {}, refetch: refetchChargingOptions, status: assetChargingOptionsApiStatus } = useAssetsChargingOptions([assetId], projectId, chargeFilter, fetchChargingData, 1);
    // Per-asset persistence.
    const { getAssetState, persisted, setChargeFilter: persistChargeFilter, setChargingOptionsResponse: persistBaseResp, setFutureChargingOptionsResponse: persistFutureResp, setTime: persistTime } = useChargingPersistenceStore((s) => ({
        getAssetState: s.getAssetState,
        persisted: s.getAssetState(assetKey),
        setChargeFilter: s.setChargeFilter,
        setChargingOptionsResponse: s.setChargingOptionsResponse,
        setFutureChargingOptionsResponse: s.setFutureChargingOptionsResponse,
        setTime: s.setTime
    }));
    const handleChargeFilter = (event) => {
        const value = event.target.value;
        if (value === chargeFilter)
            return; // no-op if unchanged
        setChargeFilter(value);
        persistChargeFilter(assetKey, value);
        if (showFutureOptions && selectedTime !== "Select time") {
            // Future mode: disable base query, trigger future refetch only
            setFetchChargingData(false);
            setFetchFutureChargeOptions(true);
            setIsLoadingFutureOptions(true);
        }
        else {
            // Normal mode: enable base query (React Query will fetch once via enabled + key change)
            setIsReloadingChargingOptions(true); // show base loader
            setFetchChargingData(true);
            // Removed manual refetch to avoid duplicate call
        }
    };
    const renderChargingOptions = () => {
        // Always fetch fresh base charging options when no prior future selection.
        // Ignore persisted base response; only future selections use persistence per requirement.
        if (assetChargingOptionsApiStatus === "pending") {
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) }));
        }
        if (assetChargingOptionsApiStatus === "error") {
            return _jsx(APIError, { refreshHandler: refetchChargingOptions });
        }
        if (assetChargingOptionsApiStatus === "success") {
            if (Object.keys(assetChargingOptionsData).length === 0 || assetChargingOptionsData?.assets?.length === 0) {
                return _jsx(NoDataMessage, {});
            }
            const updated = (assetChargingOptionsData?.assets || []).map(({ chargeOptions, ...rest }) => ({
                ...rest,
                chargeOptions: sortByChargingOptions(Array.isArray(chargeOptions) ? chargeOptions : [], sortByFilter)
            }));
            try {
                const derived = { ...assetChargingOptionsData, assets: updated };
                return (_jsx(ChargingOptions, { chargeFilter: chargeFilter, chargingOptionsData: derived, offset: offset, timezone: timezone }));
            }
            catch (error) {
                console.error("Error deriving charging options:", error);
            }
        }
        return null;
    };
    // Initialization / hydration effect
    useEffect(() => {
        if (!assetKey)
            return;
        const state = getAssetState(assetKey);
        if (lastAssetKeyRef.current !== assetKey) {
            lastAssetKeyRef.current = assetKey;
            userChangedTimePanelRef.current = false; // allow hydration for new asset
        }
        if (state.showFuture && state.selectedTimeLabel !== "Select time") {
            if (state.chargeFilter)
                setChargeFilter(state.chargeFilter);
            const now = Date.now();
            const isStale = !state.lastFutureFetchAt || now - state.lastFutureFetchAt > STALE_FUTURE_THRESHOLD_MS;
            setSelectedTime(state.selectedTimeLabel);
            if (!userChangedTimePanelRef.current) {
                setTimePanel(state.timePanel);
                const newIndex = state.timePanel === "Departure" ? 0 : state.timePanel === "Arrival" ? 1 : 2;
                setTimePanelActiveIndex(newIndex);
            }
            if (state.timeUnits)
                setTimeUnits(state.timeUnits);
            if (state.time)
                setValue("time", state.time);
            if (fetchChargingData)
                setFetchChargingData(false);
            if (isStale) {
                if (!staleRefetchTriggeredRef.current) {
                    staleRefetchTriggeredRef.current = true;
                    setShowFutureOptions(true);
                    if (!fetchFutureChargeOptions)
                        setFetchFutureChargeOptions(true);
                    if (!isLoadingFutureOptions)
                        setIsLoadingFutureOptions(true);
                }
            }
            else {
                staleRefetchTriggeredRef.current = false;
                setShowFutureOptions(true);
            }
        }
        else {
            if (fetchFutureChargeOptions || isLoadingFutureOptions || showFutureOptions)
                return;
            setShowFutureOptions(false);
            setSelectedTime("Select time");
            setTimePanel("Departure");
            setTimePanelActiveIndex(0);
            setTimeUnits(getDefaultTimeUnits());
            setValue("time", "");
            const noPriorTime = !state.showFuture || state.selectedTimeLabel === "Select time";
            if (noPriorTime && !initialBaseFetchRef.current && !fetchChargingData) {
                initialBaseFetchRef.current = true;
                persistChargeFilter(assetKey, chargeFilter);
                setIsReloadingChargingOptions(true);
                setFetchChargingData(true);
            }
        }
    }, [
        assetKey,
        getAssetState,
        searchParams,
        persisted,
        fetchChargingData,
        fetchFutureChargeOptions,
        isLoadingFutureOptions,
        showFutureOptions
    ]);
    const handleSelectTimeClick = (event) => {
        if (suppressNextOpenRef.current) {
            suppressNextOpenRef.current = false;
            event.preventDefault();
            setTimeout(() => {
                try {
                    selectTimeRef.current?.close();
                }
                catch (_) {
                    // ignore close errors
                }
            }, 0);
            return;
        }
        if (openSelectTime) {
            event.preventDefault();
            setTimeout(() => {
                selectTimeRef.current.open();
            }, 1000);
        }
    };
    const handleClearSelectedTime = (e) => {
        e.preventDefault();
        e.stopPropagation();
        safelyStopImmediatePropagation(e);
        suppressNextOpenRef.current = true;
        setValue("time", "");
        setSelectedTime("Select time");
        setShowFutureOptions(false);
        setFetchFutureChargeOptions(false);
        setFetchChargingData(true);
        setIsReloadingChargingOptions(true);
        setIsLoadingFutureOptions(false);
        userChangedTimePanelRef.current = false; // allow hydration after clear
        persistTime(assetKey, {
            selectedTimeLabel: "Select time",
            showFuture: false,
            time: "",
            timePanel,
            timeUnits
        });
        refetchChargingOptions();
    };
    const validateFutureCESTTime = (rawInput, meridiem) => {
        const CEST_TZ = "Europe/Amsterdam";
        const raw = rawInput.trim();
        if (!raw || !raw.includes(":"))
            return { canProceed: false };
        const [hStr, mStr] = raw.split(":");
        let hour = Number(hStr);
        const minute = Number(mStr);
        if (Number.isNaN(hour) || Number.isNaN(minute))
            return { canProceed: false };
        if (meridiem === "PM" && hour < 12)
            hour += 12;
        if (meridiem === "AM" && hour === 12)
            hour = 0;
        const { hour: nowH, minute: nowM } = getHourMinuteForTimezone(CEST_TZ);
        // Check for 12:00 AM specifically
        if (hour === 0 && minute === 0 && nowH >= 0 && nowM > 0) {
            return { canProceed: false, error: "SOC prediction is only available for today's date" };
        }
        if (hour * 60 + minute <= nowH * 60 + nowM) {
            return { canProceed: false, error: "Entered time should be greater than current site time" };
        }
        return { canProceed: true };
    };
    const handleApply = () => {
        const { canProceed, error } = validateFutureCESTTime(getValues("time") || "", timeUnits);
        if (!canProceed) {
            if (error)
                setTimeError(error);
            return;
        }
        setTimeError("");
        setFetchChargingData(false);
        setFetchFutureChargeOptions(true);
        setShowFutureOptions(true);
        const newTimeLabel = `${timePanel} time: ${getValues("time")} ${timeUnits} ${getTimezoneAbbr(timezone)}`;
        setSelectedTime(newTimeLabel);
        persistTime(assetKey, {
            selectedTimeLabel: newTimeLabel,
            showFuture: true,
            time: getValues("time"),
            timePanel,
            timeUnits
        });
        setIsReloadingChargingOptions(false);
        setIsLoadingFutureOptions(true);
        queryClient.cancelQueries({ queryKey: ["assetsChargingOptions"] });
        selectTimeRef.current.close();
    };
    const timePanelChange = (event) => {
        userChangedTimePanelRef.current = true;
        setTimePanelActiveIndex(event.detail.activeIndex);
        setShowFutureOptions(false);
        switch (event.detail.activeIndex) {
            case 0:
                setTimePanel("Departure");
                break;
            case 1:
                setTimePanel("Arrival");
                break;
            default:
                setTimePanel("Completion");
                break;
        }
    };
    const requestBody = prepareFutureChargeOptionsRequestBody([assetId], projectId, getValues("time") || "", timeUnits, timePanelActiveIndex);
    const { data: assetFutureChargingOptionsData = {}, refetch: refetchFutureChargingOptions, status: assetFutureChargingOptionsApiStatus } = useFutureChargingOptions(fetchFutureChargeOptions, requestBody, chargeFilter);
    const renderFutureChargingOptions = () => {
        if (assetFutureChargingOptionsApiStatus === "pending") {
            // If we have persisted data and we're not actively issuing a new fetch, show cached data instead of loader
            if (persisted.futureChargingOptionsResponse && !fetchFutureChargeOptions && !isLoadingFutureOptions) {
                const persistedResp = persisted.futureChargingOptionsResponse;
                const updatedAssets = persistedResp?.assets?.map(({ chargeOptions, ...rest }) => ({
                    ...rest,
                    chargeOptions: sortByChargingOptions(chargeOptions, sortByFilter)
                }));
                const rebuilt = { ...persistedResp, assets: updatedAssets };
                return (_jsx(FutureChargingOptions, { chargeFilter: chargeFilter, chargingOptionsData: rebuilt, offset: offset, time: persisted.time || getValues("time"), timePanel: persisted.timePanel || timePanel, timeUnits: persisted.timeUnits || timeUnits, timezone: timezone }));
            }
            return (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) }));
        }
        if (assetFutureChargingOptionsApiStatus === "error") {
            // Fallback to persisted data if exists
            if (persisted.futureChargingOptionsResponse) {
                const persistedResp = persisted.futureChargingOptionsResponse;
                const updatedAssets = persistedResp?.assets?.map(({ chargeOptions, ...rest }) => ({
                    ...rest,
                    chargeOptions: sortByChargingOptions(chargeOptions, sortByFilter)
                }));
                const rebuilt = { ...persistedResp, assets: updatedAssets };
                return (_jsx(FutureChargingOptions, { chargeFilter: chargeFilter, chargingOptionsData: rebuilt, offset: offset, time: persisted.time || getValues("time"), timePanel: persisted.timePanel || timePanel, timeUnits: persisted.timeUnits || timeUnits, timezone: timezone }));
            }
            return _jsx(APIError, { refreshHandler: refetchFutureChargingOptions });
        }
        if (assetFutureChargingOptionsApiStatus === "success") {
            if (Object.keys(assetFutureChargingOptionsData).length === 0 ||
                assetFutureChargingOptionsData?.assets?.length === 0) {
                return _jsx(NoDataMessage, {});
            }
            const updatedAssets = assetFutureChargingOptionsData?.assets?.map(({ chargeOptions, ...rest }) => ({
                ...rest,
                chargeOptions: sortByChargingOptions(chargeOptions, sortByFilter)
            }));
            const derived = { ...assetFutureChargingOptionsData, assets: updatedAssets };
            return (_jsx(FutureChargingOptions, { chargeFilter: chargeFilter, chargingOptionsData: derived, offset: offset, time: getValues("time"), timePanel: timePanel, timeUnits: timeUnits, timezone: timezone }));
        }
        // Idle but maybe persisted data
        if (persisted.futureChargingOptionsResponse) {
            const persistedResp = persisted.futureChargingOptionsResponse;
            const updatedAssets = persistedResp?.assets?.map(({ chargeOptions, ...rest }) => ({
                ...rest,
                chargeOptions: sortByChargingOptions(chargeOptions, sortByFilter)
            }));
            const derived = { ...persistedResp, assets: updatedAssets };
            return (_jsx(FutureChargingOptions, { chargeFilter: chargeFilter, chargingOptionsData: derived, offset: offset, time: persisted.time || getValues("time"), timePanel: persisted.timePanel || timePanel, timeUnits: persisted.timeUnits || timeUnits, timezone: timezone }));
        }
        return null;
    };
    useEffect(() => {
        if (fetchFutureChargeOptions) {
            setFetchFutureChargeOptions(false);
            setShowFutureOptions(true);
        }
        if (assetFutureChargingOptionsApiStatus === "success" || assetFutureChargingOptionsApiStatus === "error") {
            setIsLoadingFutureOptions(false); // Stop loader
        }
        if (assetFutureChargingOptionsApiStatus === "pending" && showFutureOptions && fetchFutureChargeOptions) {
            // Only show loader for an explicit new fetch (not cached display)
            if (!isLoadingFutureOptions)
                setIsLoadingFutureOptions(true);
        }
        else if (assetFutureChargingOptionsApiStatus !== "pending" && isLoadingFutureOptions) {
            setIsLoadingFutureOptions(false);
        }
        // Persist only after a successful future charging API call (per requirement)
        if (assetFutureChargingOptionsApiStatus === "success") {
            // Persist time selection (ensure we have latest user input)
            const timeValue = getValues("time");
            const label = `${timePanel} time: ${timeValue} ${timeUnits} ${getTimezoneAbbr(timezone)}`;
            persistTime(assetKey, {
                selectedTimeLabel: label,
                showFuture: true,
                time: timeValue,
                timePanel,
                timeUnits
            });
            persistFutureResp(assetKey, assetFutureChargingOptionsData);
        }
    }, [
        assetFutureChargingOptionsApiStatus,
        assetFutureChargingOptionsData,
        fetchFutureChargeOptions,
        getValues,
        persistFutureResp,
        persistTime,
        timePanel,
        timeUnits,
        timezone
    ]);
    useEffect(() => {
        if (assetChargingOptionsApiStatus === "success") {
            persistBaseResp(assetKey, assetChargingOptionsData);
            if (fetchChargingData)
                setFetchChargingData(false);
        }
        else if (assetChargingOptionsApiStatus === "error" && fetchChargingData) {
            setFetchChargingData(false);
        }
    }, [assetChargingOptionsApiStatus, assetChargingOptionsData, fetchChargingData, persistBaseResp, assetKey]);
    // Turn off manual loader once the refetch completes
    useEffect(() => {
        if (isReloadingChargingOptions &&
            (assetChargingOptionsApiStatus === "success" || assetChargingOptionsApiStatus === "error")) {
            setIsReloadingChargingOptions(false);
        }
    }, [assetChargingOptionsApiStatus, isReloadingChargingOptions]);
    return (_jsxs("div", { children: [_jsxs("div", { className: `flex justify-between items-center p-2 ${showTitle && "ops-planner-container"}`, children: [showTitle && (_jsx("div", { children: _jsx(CatHeading, { className: "flex", "data-test": "charging-options", variant: "title", children: "Charging Options" }) })), _jsx("div", {}), _jsx("div", { className: "flex gap-4 flex-nowrap w-[780px]", children: _jsxs(CatFieldset, { orientation: "horizontal", children: [_jsx(CatRadioField, { checked: chargeFilter === "80", className: "mr-2", name: "charging-percentage", onBlRadioChange: handleChargeFilter, value: "80", children: "Charge to 80%" }), _jsx(CatRadioField, { checked: chargeFilter === "100", className: "mr-2", name: "charging-percentage", onBlRadioChange: handleChargeFilter, value: "100", children: "Charge to 100%" }), _jsx(CatDropdown, { ariaDescribedBy: "charging-options-dropdown-field", className: "w-[8rem]", fieldId: "charging-options-dropdown-field", placeholder: "Sorting option(s)", size: "sm", value: sortByFilter, children: _jsxs(CatList, { children: [_jsx(CatListItem, { onBlSelect: () => {
                                                    setSortByFilter("fastest");
                                                }, value: {
                                                    label: "Fastest",
                                                    value: "Fastest"
                                                } }), _jsx(CatListItem, { onBlSelect: () => {
                                                    setSortByFilter("fully charged");
                                                }, value: {
                                                    label: "Fully charged",
                                                    value: "Fully charged"
                                                } }), _jsx(CatListItem, { onBlSelect: () => {
                                                    setSortByFilter("closest ess");
                                                }, value: {
                                                    label: "Closest ESS",
                                                    value: "Closest ESS"
                                                } })] }) }), appConfig?.release_1_1?.showOpsPlannerPhaseTwo &&
                                    appConfig?.release_1_1?.showSocFutureChargingOpsPlanner && (_jsxs(CatContextualMenu, { onBlContextualMenuOpen: handleSelectTimeClick, position: "bottom", ref: selectTimeRef, children: [_jsx(CatButton, { className: "w-[300px] h-[32px] inline-flex", "data-test": "select-time-button", fullWidth: true, size: "sm", slot: "trigger", children: _jsxs("div", { className: "w-[300px] flex justify-between font-normal items-center px-2 font-sans text-base overflow-hidden", children: [_jsx("span", { className: "text-left pr-6 whitespace-nowrap flex-shrink min-w-0", children: selectedTime }), _jsxs("div", { className: "flex items-center gap-1 flex-shrink-0", children: [selectedTime !== "Select time" && (_jsx("span", { "aria-label": "Clear selected time", className: "inline-flex justify-center items-center pr-1 h-3 w-3 cursor-pointer", onClick: handleClearSelectedTime, onMouseDown: handleClearSelectedTime, role: "button", children: _jsx(CatIconClose, { className: "h-[16px] w-[16px] mr-2 ml-2 mb-0" }) })), _jsx(CatIconClock, { className: "inline-flex justify-center pl-1 h-3.5" })] })] }) }), _jsxs("div", { className: "w-[335px]", children: [_jsx("h2", { className: "w-full h-[40px] flex items-center justify-center", children: "Time" }), _jsx("hr", {}), _jsx("div", { className: "flex p-2", children: _jsxs(CatSegmentedControl, { activeIndex: timePanelActiveIndex, className: "relative", onBlSegmentItemChange: timePanelChange, children: [_jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "departure-title-tab", children: "Departure" }), _jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "arrival-tab", children: "Arrival" }), _jsx(CatSegmentedControlItem, { className: "flex-1", "data-test": "completion-tab", children: "Completion" })] }) }), _jsx("hr", {}), _jsxs("form", { onSubmit: (e) => {
                                                        e.preventDefault();
                                                        if (!timeError) {
                                                            handleApply();
                                                        }
                                                    }, children: [_jsxs("div", { className: "flex items-start gap-2 p-2", children: [_jsxs("div", { className: "flex flex-col w-full min-h-[64px]", children: [_jsx("label", { className: "text-xs font-semibold mb-1", children: "Time (required)" }), _jsx(Controller, { control: control, name: "time", render: ({ field: { onBlur, onChange, value } }) => (_jsx(CatInputField, { "data-test": "asset-name", errorNote: timeError, fieldId: "text-field-11", onBlChange: (e) => {
                                                                                    onChange(e);
                                                                                    // Validate as user types
                                                                                    const inputValue = e.detail?.value ||
                                                                                        e.target?.value ||
                                                                                        "";
                                                                                    if (inputValue.trim()) {
                                                                                        const { canProceed, error } = validateFutureCESTTime(inputValue, timeUnits);
                                                                                        if (!canProceed && error) {
                                                                                            setTimeError(error);
                                                                                        }
                                                                                        else {
                                                                                            setTimeError("");
                                                                                        }
                                                                                    }
                                                                                    else {
                                                                                        setTimeError("");
                                                                                    }
                                                                                }, onBlur: onBlur, onKeyDown: (e) => {
                                                                                    if (e.key === "Enter") {
                                                                                        e.preventDefault();
                                                                                        if (!timeError) {
                                                                                            handleApply();
                                                                                        }
                                                                                    }
                                                                                }, placeholder: getPlaceholderTime(timezone), size: "sm", status: timeError ? "error" : undefined, value: value })) }), timeError && (_jsx("div", { className: "text-xs text-red-600 mt-1", "data-test": "time-error-text", children: timeError }))] }), _jsxs("div", { className: "flex flex-col w-[80px]", children: [_jsx("label", { className: "text-xs font-semibold mb-1", children: "AM/PM" }), _jsxs("select", { className: "border border-gray-300 rounded px-1 py-1", onChange: (event) => {
                                                                                const newTimeUnits = event.target.value;
                                                                                if (timeUnits !== newTimeUnits) {
                                                                                    setTimeUnits(newTimeUnits);
                                                                                    // Revalidate time when AM/PM changes
                                                                                    const currentTime = getValues("time");
                                                                                    if (currentTime?.trim()) {
                                                                                        const { canProceed, error } = validateFutureCESTTime(currentTime, newTimeUnits);
                                                                                        if (!canProceed && error) {
                                                                                            setTimeError(error);
                                                                                        }
                                                                                        else {
                                                                                            setTimeError("");
                                                                                        }
                                                                                    }
                                                                                }
                                                                            }, value: timeUnits, children: [_jsx("option", { children: "AM" }), _jsx("option", { children: "PM" })] })] })] }), _jsxs("div", { className: "flex justify-start py-4", children: [_jsx(CatButton, { className: "pl-4", "data-testid": "Apply-button", disabled: !!timeError, onClick: handleApply, type: "submit", variant: "primary", children: "Apply" }), _jsx(CatButton, { onClick: () => {
                                                                        selectTimeRef.current.close();
                                                                    }, size: "sm", type: "button", variant: "ghost", children: "Cancel" })] })] })] })] }))] }) })] }), showTitle && appConfig?.release_1_1?.showSocFutureChargingOpsPlanner && (_jsxs("div", { className: "local-time-cest-format", "data-test": "charging-option-cest-time", children: ["Site time:", _jsx(LiveLocalCESTTime, { timezone: timezone })] })), _jsx("div", { className: `overflow-y-auto ${sizeSmall ? "max-h-[300px]" : "max-h-[500px]"}`, children: showFutureOptions ? (isLoadingFutureOptions ? (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) })) : (renderFutureChargingOptions())) : isReloadingChargingOptions && !persisted.chargingOptionsResponse ? (_jsx("div", { className: "flex justify-center items-center h-full min-h-[28vh] w-full", children: _jsx(CatLoadingIndicator, {}) })) : (renderChargingOptions()) })] }));
};
export default ChargingOptionsWrapper;
